const AWS_WAF_STAGE_SCRIPT_ID = 'aws-waf-stage-challenge'

export const AWS_WAF_TOKEN_HEADER = 'x-aws-waf-token'
export const AWS_WAF_TOKEN_COOKIE = 'aws-waf-token'

export const AWS_WAF_STAGE_INTEGRATION_URL =
  'https://1a7223512c3a.edge.sdk.awswaf.com/1a7223512c3a/101cba36f101/'
export const AWS_WAF_STAGE_CHALLENGE_URL = `${AWS_WAF_STAGE_INTEGRATION_URL}challenge.js`
export const AWS_WAF_STAGE_TOKEN_DOMAIN = 'stage.atm-aws.com'

interface AwsWafIntegration {
  fetch: typeof fetch
  getToken: () => Promise<string>
  hasToken: () => boolean
}

declare global {
  interface Window {
    AwsWafIntegration?: AwsWafIntegration
    awsWafCookieDomainList?: string[]
  }
}

export function isAwsWafStageEnvironment(): boolean {
  return process.env.NEXT_PUBLIC_VERCEL_ENV === 'preview'
}

export function awsWafTokenFromRequest(
  headerValue: string | string[] | undefined,
  cookieValue?: string,
): string | undefined {
  const headerToken = Array.isArray(headerValue) ? headerValue[0] : headerValue
  return headerToken?.trim() || cookieValue?.trim() || undefined
}

// Loads AWS WAF's challenge script on Vercel previews. Fail-open on purpose:
// production and local never load it, and a load failure changes nothing.
export async function loadAwsWafStageIntegration(): Promise<boolean> {
  if (!isAwsWafStageEnvironment() || typeof window === 'undefined') {
    return false
  }

  window.awsWafCookieDomainList = [AWS_WAF_STAGE_TOKEN_DOMAIN]

  if (window.AwsWafIntegration) {
    return true
  }

  let script = document.getElementById(
    AWS_WAF_STAGE_SCRIPT_ID,
  ) as HTMLScriptElement | null

  if (script?.dataset.loaded === 'true') {
    return Boolean(window.AwsWafIntegration)
  }

  if (!script) {
    script = document.createElement('script')
    script.id = AWS_WAF_STAGE_SCRIPT_ID
    script.src = AWS_WAF_STAGE_CHALLENGE_URL
    script.defer = true
    document.head.appendChild(script)
  }

  return new Promise<boolean>((resolve) => {
    const handleLoad = () => {
      script.dataset.loaded = 'true'
      resolve(Boolean(window.AwsWafIntegration))
    }
    const handleError = () => {
      script.remove()
      resolve(false)
    }

    script.addEventListener('load', handleLoad, { once: true })
    script.addEventListener('error', handleError, { once: true })
  })
}

// AWS's fetch wrapper for browser-to-API calls on stage; it attaches the WAF
// token. Everywhere else, SSR included, this is native fetch.
export const awsWafStageFetch: typeof fetch = async (input, init) => {
  const integrationReady = await loadAwsWafStageIntegration()

  if (integrationReady && window.AwsWafIntegration) {
    return window.AwsWafIntegration.fetch(input, init)
  }

  return fetch(input, init)
}
